import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    Chip,
    CircularProgress,
    Checkbox,
    TableRow,
    TableCell,
    Button,
    FormControl,
    FormControlLabel,
    Select,
    MenuItem,
    Collapse,
    Divider,
    Switch,
    Alert
} from "@mui/material";
import MuiDialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import {
    Search as SearchIcon,
    Close as CloseIcon,
    Person as PersonIcon,
    Settings as SettingsIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon
} from "@mui/icons-material";
import Dialog from "./Dialog";
import ModernDataTable from "../ModernDataTable";
import { getClients } from "../../services/TraineeService";
import { getTrainers } from "../../services/TrainerService";
import { getFilteredTraineeCount } from "../../services/PlanSerice";
import { useCompany } from "../../contexts/CompanyContext";
import moment from "moment";
import {RTL} from "../../cache/CacheRtl";

const PAGE_SIZE = 20;

/**
 * Reusable component for searching and selecting multiple users/trainees
 *
 * Props:
 * - isOpen: boolean - Whether the dialog is open
 * - onClose: () => void - Callback when dialog is closed
 * - onConfirm: (selectedUsers: User[], options?: { includeRmWeek: boolean, advancedSettings?: object }) => void - Callback when users are confirmed
 * - title: string - Dialog title (default: "בחירת מתאמנים")
 * - confirmButtonText: string - Text for confirm button (default: "אישור")
 * - isLoading: boolean - External loading state (optional)
 * - excludeUserIds: string[] - User IDs to exclude from the list (optional)
 * - showRmWeekOption: boolean - Whether to show the RM Week checkbox (optional, default: false)
 * - showAdvancedSettings: boolean - Whether to show advanced program settings (optional, default: false)
 * - templateId: number - If provided, only show trainees who have access to this template (optional)
 * - companyOverride: object - If provided, use this company instead of the one from context (optional)
 * - trainerIdOverride: number - If provided, lock trainer filter to this trainer ID (optional)
 */
const UserMultiSelectDialog = ({
    isOpen,
    onClose,
    onConfirm,
    onSelectAllConfirm = null,
    showSelectAll = false,
    title = "בחירת מתאמנים",
    confirmButtonText = "אישור",
    isLoading: externalLoading = false,
    excludeUserIds = [],
    showRmWeekOption = false,
    showAdvancedSettings = false,
    templateId = null,
    companyOverride = null,
    trainerIdOverride = null
}) => {
    const { company: contextCompany } = useCompany();
    const company = companyOverride || contextCompany;

    // If trainerIdOverride is provided, lock the trainer filter
    const isTrainerLocked = trainerIdOverride !== null;

    // State for clients list
    const [clientsState, setClientsState] = useState({
        clients: [],
        pageId: 0,
        isLoading: false,
        hasMoreData: true,
        totalNumberOfElements: 0
    });

    // State for selected users
    const [selectedUsers, setSelectedUsers] = useState([]);

    // State for RM Week option
    const [includeRmWeek, setIncludeRmWeek] = useState(false);

    // State for advanced settings
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [advancedSettings, setAdvancedSettings] = useState({
        automatic: true,
        useProgressionAlgorithm: true,
        useRir: true
    });

    // State for filters
    const [keyword, setKeyword] = useState("");
    const [gender, setGender] = useState("ALL");
    const [trainerId, setTrainerId] = useState(trainerIdOverride || 0);
    const [trainers, setTrainers] = useState([]);

    // State for "Select All" functionality
    const [selectAllCount, setSelectAllCount] = useState(0);
    const [loadingSelectAllCount, setLoadingSelectAllCount] = useState(false);
    const [selectAllDialogOpen, setSelectAllDialogOpen] = useState(false);

    // Ref to track if initial load has happened
    const initialLoadDone = useRef(false);
    // Ref to track current loading state to prevent duplicate fetches
    const isLoadingRef = useRef(false);

    // Fetch trainers when dialog opens or company changes
    useEffect(() => {
        if (isOpen && company?.id) {
            getTrainers(company.id)
                .then(res => setTrainers(res.data || []))
                .catch(err => console.error("Error fetching trainers:", err));
        }
    }, [isOpen, company?.id]);

    // Fetch clients function
    const fetchClients = useCallback((pageId = 0, reset = false) => {
        // Prevent duplicate fetches
        if (isLoadingRef.current) {
            return;
        }

        isLoadingRef.current = true;
        setClientsState(prev => ({
            ...prev,
            isLoading: true
        }));

        getClients(
            pageId,
            PAGE_SIZE,
            keyword || null,
            trainerId,
            gender,
            "ALL",
            company,
            templateId
        )
            .then(res => {
                const newClients = res.data.content.filter(
                    client => !excludeUserIds.includes(client.id)
                );

                // Check if we have more data - if returned less than page size, no more data
                const hasMore = res.data.content.length >= PAGE_SIZE;

                setClientsState(prev => ({
                    clients: reset ? newClients : [...prev.clients, ...newClients],
                    pageId: pageId + 1,
                    isLoading: false,
                    hasMoreData: hasMore,
                    totalNumberOfElements: res.data.totalElements || 0
                }));
            })
            .catch(err => {
                console.error("Error fetching clients:", err);
                setClientsState(prev => ({
                    ...prev,
                    isLoading: false,
                    hasMoreData: false
                }));
            })
            .finally(() => {
                isLoadingRef.current = false;
            });
    }, [keyword, trainerId, gender, company, excludeUserIds, templateId]);

    // Initial fetch when dialog opens
    useEffect(() => {
        if (isOpen && !initialLoadDone.current) {
            initialLoadDone.current = true;
            fetchClients(0, true);
        } else if (!isOpen) {
            // Reset state when dialog closes
            initialLoadDone.current = false;
            isLoadingRef.current = false;
            setClientsState({
                clients: [],
                pageId: 0,
                isLoading: false,
                hasMoreData: true,
                totalNumberOfElements: 0
            });
            setSelectedUsers([]);
            setIncludeRmWeek(false);
            setAdvancedOpen(false);
            setAdvancedSettings({
                automatic: true,
                useProgressionAlgorithm: true,
                useRir: true
            });
            setKeyword("");
            setGender("ALL");
            setTrainerId(trainerIdOverride || 0);
        }
    }, [isOpen, trainerIdOverride]); // eslint-disable-line react-hooks/exhaustive-deps

    // Refetch when filters change (with debounce for keyword)
    useEffect(() => {
        // Skip initial render - initial load is handled separately
        if (!isOpen || !initialLoadDone.current) {
            return;
        }

        const timer = setTimeout(() => {
            fetchClients(0, true);
        }, 300);

        return () => clearTimeout(timer);
    }, [keyword, trainerId, gender]); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle user selection toggle
    const toggleUserSelection = (user) => {
        setSelectedUsers(prev => {
            const isSelected = prev.some(u => u.id === user.id);
            if (isSelected) {
                return prev.filter(u => u.id !== user.id);
            } else {
                return [...prev, user];
            }
        });
    };

    // Remove user from selection
    const removeUser = (userId) => {
        setSelectedUsers(prev => prev.filter(u => u.id !== userId));
    };

    // Handle confirm
    const handleConfirm = () => {
        const options = {};
        if (showRmWeekOption) {
            options.includeRmWeek = includeRmWeek;
        }
        if (showAdvancedSettings) {
            options.advancedSettings = advancedSettings;
        }
        onConfirm(selectedUsers, options);
    };

    // Fetch next page for infinite scroll
    const fetchNextPage = useCallback(() => {
        if (!isLoadingRef.current && clientsState.hasMoreData && !clientsState.isLoading) {
            fetchClients(clientsState.pageId, false);
        }
    }, [clientsState.hasMoreData, clientsState.isLoading, clientsState.pageId, fetchClients]);

    // Fetch count for "Select All" feature
    const fetchSelectAllCount = useCallback(async () => {
        if (!templateId) return;

        setLoadingSelectAllCount(true);
        try {
            const response = await getFilteredTraineeCount(
                templateId,
                gender,
                keyword,
                trainerId,
                company?.id
            );
            setSelectAllCount(response.data?.count || 0);
        } catch (err) {
            console.error("Error fetching trainee count:", err);
            setSelectAllCount(0);
        } finally {
            setLoadingSelectAllCount(false);
        }
    }, [templateId, gender, keyword, trainerId, company?.id]);

    // Handle "Select All" confirmation
    const handleSelectAllConfirm = () => {
        setSelectAllDialogOpen(false);
        if (onSelectAllConfirm) {
            onSelectAllConfirm(
                {
                    filters: { gender, keyword, trainerId, companyId: company?.id },
                    count: selectAllCount
                },
                {
                    includeRmWeek,
                    advancedSettings
                }
            );
        }
    };

    // Format date helper
    const formatDate = (timestamp) => {
        if (!timestamp) return "-";
        return moment.unix(timestamp).format("DD/MM/YY");
    };

    // Get gender display
    const getGenderDisplay = (gender) => {
        if (gender === "FEMALE") return "נ";
        if (gender === "MALE") return "ז";
        return "-";
    };

    // Get gender colors
    const getGenderColor = (gender) => {
        if (gender === "FEMALE") return { backgroundColor: "#FDF2F8", color: "#BE185D" };
        if (gender === "MALE") return { backgroundColor: "#EFF6FF", color: "#1D4ED8" };
        return { backgroundColor: "#F3F4F6", color: "#6B7280" };
    };

    // Setting row component
    const SettingRow = ({label, checked, onChange, statusLabel}) => (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5 }}>
            <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#1E293B" }}>
                {label}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Switch
                    checked={checked}
                    onChange={onChange}
                    size="small"
                    sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#57837E' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#57837E' },
                    }}
                />
                <Typography sx={{ fontSize: "11px", color: "#64748B", minWidth: "50px" }}>
                    {statusLabel}
                </Typography>
            </Box>
        </Box>
    );

    const columns = [
        { header: "", field: "checkbox", width: "8%", align: "center" },
        { header: "שם", field: "name", width: "40%", align: "right" },
        { header: "מגדר", field: "gender", width: "15%", align: "center" },
        { header: "אימון אחרון", field: "lastTrained", width: "20%", align: "center" },
        { header: "סטטוס", field: "status", width: "17%", align: "center" }
    ];

    const dialogActions = (
        <Box sx={{ display: "flex", gap: 2, width: "100%", justifyContent: "flex-start" }}>
            <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                    color: "#64748B",
                    borderColor: "#E2E8F0",
                    "&:hover": {
                        borderColor: "#CBD5E1",
                        backgroundColor: "#F8FAFC"
                    }
                }}
            >
                ביטול
            </Button>
            <Button
                variant="contained"
                onClick={handleConfirm}
                disabled={selectedUsers.length === 0 || externalLoading}
                sx={{
                    backgroundColor: "#10B981",
                    "&:hover": {
                        backgroundColor: "#059669"
                    },
                    "&:disabled": {
                        backgroundColor: "#E2E8F0"
                    }
                }}
            >
                {externalLoading ? (
                    <CircularProgress size={20} sx={{ color: "white" }} />
                ) : (
                    `${confirmButtonText} (${selectedUsers.length})`
                )}
            </Button>
        </Box>
    );

    return (
    <>
        <Dialog
            title={title}
            isOpen={isOpen}
            close={onClose}
            actions={dialogActions}
            showCloseButton={true}
        >
            <Box sx={{ direction: "rtl", minHeight: "500px" }}>
                {/* Selected users chips */}
                {selectedUsers.length > 0 && (
                    <Box sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        p: 2,
                        backgroundColor: "#F0FDF4",
                        borderRadius: "8px",
                        mb: 2
                    }}>
                        <Typography sx={{
                            fontSize: "13px",
                            color: "#166534",
                            fontWeight: 600,
                            width: "100%",
                            mb: 1
                        }}>
                            נבחרו {selectedUsers.length} מתאמנים:
                        </Typography>
                        {selectedUsers.map(user => (
                            <RTL key={user.id}>
                            <Chip
                                label={user.name}
                                onDelete={() => removeUser(user.id)}
                                deleteIcon={<CloseIcon sx={{ fontSize: "14px !important" }} />}
                                icon={<PersonIcon sx={{ fontSize: "14px !important" }} />}
                                size="small"
                                sx={{
                                    backgroundColor: "#DCFCE7",
                                    color: "#166534",
                                    fontSize: "12px",
                                    "& .MuiChip-deleteIcon": {
                                        color: "#166534",
                                        "&:hover": {
                                            color: "#14532D"
                                        }
                                    }
                                }}
                            />
                            </RTL>
                        ))}
                    </Box>
                )}

                {/* Options Section - RM Week and Advanced Settings */}
                {(showRmWeekOption || showAdvancedSettings) && (
                    <Box sx={{
                        p: 2,
                        backgroundColor: "#F8FAFC",
                        borderRadius: "8px",
                        mb: 2,
                        border: "1px solid #E2E8F0"
                    }}>
                        {/* RM Week Option */}
                        {showRmWeekOption && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={includeRmWeek}
                                        onChange={(e) => setIncludeRmWeek(e.target.checked)}
                                        sx={{
                                            color: "#64748B",
                                            "&.Mui-checked": {
                                                color: "#57837E"
                                            }
                                        }}
                                    />
                                }
                                label={
                                    <Typography sx={{ fontSize: "14px", color: "#1E293B" }}>
                                        כלול שבוע RM (שבוע הכנה לתוכנית)
                                    </Typography>
                                }
                            />
                        )}

                        {/* Advanced Settings */}
                        {showAdvancedSettings && (
                            <>
                                {showRmWeekOption && <Divider sx={{ my: 1.5 }} />}
                                <Box
                                    onClick={() => setAdvancedOpen(prev => !prev)}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        cursor: "pointer",
                                        color: "#64748B",
                                        py: 0.5,
                                        "&:hover": { color: "#1E293B" }
                                    }}
                                >
                                    <SettingsIcon sx={{ fontSize: 16 }} />
                                    <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                                        הגדרות מתקדמות
                                    </Typography>
                                    {advancedOpen ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                                </Box>

                                <Collapse in={advancedOpen} timeout="auto">
                                    <Box sx={{
                                        mt: 1.5,
                                        p: 2,
                                        backgroundColor: "white",
                                        borderRadius: "8px",
                                        border: "1px solid #E2E8F0"
                                    }}>
                                        <SettingRow
                                            label="התקדמות אוטומטית"
                                            checked={advancedSettings.automatic}
                                            onChange={(e) => setAdvancedSettings(prev => ({...prev, automatic: e.target.checked}))}
                                            statusLabel={advancedSettings.automatic ? "התקדמות אוטומטית" : "התקדמות ידנית"}
                                        />
                                        <Divider sx={{ my: 0.5 }} />

                                        <SettingRow
                                            label="שימוש באלגוריתם התקדמות"
                                            checked={advancedSettings.useProgressionAlgorithm}
                                            onChange={(e) => setAdvancedSettings(prev => ({...prev, useProgressionAlgorithm: e.target.checked}))}
                                            statusLabel={advancedSettings.useProgressionAlgorithm ? "מופעל" : "מבוטל"}
                                        />
                                        <Divider sx={{ my: 0.5 }} />

                                        <SettingRow
                                            label="שימוש RIR"
                                            checked={advancedSettings.useRir}
                                            onChange={(e) => setAdvancedSettings(prev => ({...prev, useRir: e.target.checked}))}
                                            statusLabel={advancedSettings.useRir ? "RIR פעיל" : "מבוטל"}
                                        />
                                    </Box>
                                </Collapse>
                            </>
                        )}
                    </Box>
                )}

                {/* Search and filters */}
                <Box sx={{
                    display: "flex",
                    gap: 2,
                    mb: 2,
                    flexWrap: "wrap",
                    alignItems: "center"
                }}>
                    <TextField
                        placeholder="חיפוש לפי שם..."
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        size="small"
                        sx={{
                            flex: 1,
                            minWidth: "200px",
                            "& .MuiOutlinedInput-root": {
                                backgroundColor: "#F8FAFC",
                                borderRadius: "8px",
                                "& fieldset": {
                                    borderColor: "#E2E8F0"
                                },
                                "&:hover fieldset": {
                                    borderColor: "#CBD5E1"
                                },
                                "&.Mui-focused fieldset": {
                                    borderColor: "#10B981"
                                }
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: "#64748B", fontSize: "20px" }} />
                                </InputAdornment>
                            )
                        }}
                    />
                    <RTL>
                    <FormControl size="small" sx={{ minWidth: "120px" }}>
                        <Select
                            value={gender}
                            onChange={(e) => setGender(e.target.value)}
                            sx={{
                                backgroundColor: "#F8FAFC",
                                borderRadius: "8px",
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#E2E8F0"
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#CBD5E1"
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#10B981"
                                }
                            }}
                        >
                            <MenuItem value="ALL">כל המגדרים</MenuItem>
                            <MenuItem value="MALE">זכר</MenuItem>
                            <MenuItem value="FEMALE">נקבה</MenuItem>
                        </Select>
                    </FormControl>
                    </RTL>

                    {trainers.length > 0 && !isTrainerLocked && (
                        <RTL>
                        <FormControl size="small" sx={{ minWidth: "150px" }}>
                            <Select
                                value={trainerId}
                                onChange={(e) => setTrainerId(e.target.value)}
                                sx={{
                                    backgroundColor: "#F8FAFC",
                                    borderRadius: "8px",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#E2E8F0"
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#CBD5E1"
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#10B981"
                                    }
                                }}
                            >
                                <MenuItem value={0}>כל המאמנים</MenuItem>
                                {trainers.map(trainer => (
                                    <MenuItem key={trainer.id} value={trainer.id}>
                                        {trainer.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        </RTL>
                    )}
                </Box>

                {/* Select All Section */}
                {showSelectAll && templateId && onSelectAllConfirm && (
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: 2,
                        mb: 2,
                        backgroundColor: "#F0F9FF",
                        borderRadius: "8px",
                        border: "1px solid #0EA5E9"
                    }}>
                        <Box>
                            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#0369A1" }}>
                                בחירה מרוכזת
                            </Typography>
                            <Typography sx={{ fontSize: "12px", color: "#64748B" }}>
                                הקצה תוכנית לכל המתאמנים המתאימים לסינון הנוכחי
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            onClick={() => {
                                fetchSelectAllCount();
                                setSelectAllDialogOpen(true);
                            }}
                            disabled={loadingSelectAllCount}
                            sx={{
                                backgroundColor: "#0EA5E9",
                                "&:hover": { backgroundColor: "#0284C7" }
                            }}
                        >
                            {loadingSelectAllCount ? (
                                <CircularProgress size={20} sx={{ color: "white" }} />
                            ) : (
                                "בחר הכל"
                            )}
                        </Button>
                    </Box>
                )}

                {/* Clients table */}
                <Box
                    sx={{ maxHeight: "400px", overflow: "auto" }}
                    onScroll={(e) => {
                        const { scrollTop, scrollHeight, clientHeight } = e.target;
                        // Load more when user scrolls near bottom (within 50px)
                        if (scrollHeight - scrollTop - clientHeight < 50) {
                            fetchNextPage();
                        }
                    }}
                >
                    <ModernDataTable
                        data={clientsState.clients}
                        columns={columns}
                        totalCount={clientsState.totalNumberOfElements}
                        isLoading={clientsState.isLoading}
                        hasMore={clientsState.hasMoreData}
                        onLoadMore={fetchNextPage}
                        useInfiniteScroll={false}
                        emptyTitle="לא נמצאו מתאמנים"
                        emptySubtitle="נסה לשנות את הסינון"
                        containerSx={{ margin: 0 }}
                    >
                        {clientsState.clients.map((client) => (
                            <TableRow
                                key={client.id}
                                onClick={() => toggleUserSelection(client)}
                                sx={{
                                    cursor: "pointer",
                                    backgroundColor: selectedUsers.some(u => u.id === client.id)
                                        ? "#F0FDF4"
                                        : "transparent",
                                    "&:hover": {
                                        backgroundColor: selectedUsers.some(u => u.id === client.id)
                                            ? "#DCFCE7"
                                            : "#F8FAFC"
                                    },
                                    borderBottom: "1px solid #E2E8F0"
                                }}
                            >
                                <TableCell
                                    width="8%"
                                    align="center"
                                    sx={{ padding: "12px 8px", borderBottom: "none" }}
                                >
                                    <Checkbox
                                        size="small"
                                        checked={selectedUsers.some(u => u.id === client.id)}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleUserSelection(client);
                                        }}
                                        sx={{
                                            color: "#64748B",
                                            "&.Mui-checked": {
                                                color: "#10B981"
                                            }
                                        }}
                                    />
                                </TableCell>

                                <TableCell
                                    width="40%"
                                    align="right"
                                    sx={{ padding: "12px 16px", borderBottom: "none" }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "14px",
                                            fontWeight: 600,
                                            color: "#1E293B"
                                        }}
                                    >
                                        {client.name}
                                    </Typography>
                                </TableCell>

                                <TableCell
                                    width="15%"
                                    align="center"
                                    sx={{ padding: "12px 16px", borderBottom: "none" }}
                                >
                                    <Chip
                                        label={getGenderDisplay(client.gender)}
                                        size="small"
                                        sx={{
                                            minWidth: "28px",
                                            height: "24px",
                                            fontSize: "11px",
                                            fontWeight: 600,
                                            ...getGenderColor(client.gender)
                                        }}
                                    />
                                </TableCell>

                                <TableCell
                                    width="20%"
                                    align="center"
                                    sx={{ padding: "12px 16px", borderBottom: "none" }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "13px",
                                            color: client.lastTrained ? "#64748B" : "#94A3B8"
                                        }}
                                    >
                                        {formatDate(client.lastTrained)}
                                    </Typography>
                                </TableCell>

                                <TableCell
                                    width="17%"
                                    align="center"
                                    sx={{ padding: "12px 16px", borderBottom: "none" }}
                                >
                                    <Chip
                                        label={client.enabled ? "פעיל" : "לא פעיל"}
                                        size="small"
                                        sx={{
                                            backgroundColor: client.enabled ? "#DCFCE7" : "#FEE2E2",
                                            color: client.enabled ? "#166534" : "#DC2626",
                                            fontSize: "11px",
                                            fontWeight: 600
                                        }}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </ModernDataTable>

                    {/* Loading indicator */}
                    {clientsState.isLoading && (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                            <CircularProgress size={24} sx={{ color: "#10B981" }} />
                        </Box>
                    )}
                </Box>
            </Box>
        </Dialog>

        {/* Select All Confirmation Dialog */}
        {showSelectAll && templateId && onSelectAllConfirm && (
        <MuiDialog
            open={selectAllDialogOpen}
            onClose={() => setSelectAllDialogOpen(false)}
            sx={{ direction: 'rtl' }}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle sx={{ textAlign: 'right', fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                אישור הקצאה מרוכזת
            </DialogTitle>
            <DialogContent>
                {loadingSelectAllCount ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Typography sx={{ mb: 2 }}>
                            פעולה זו תקצה את התוכנית ל-
                            <strong style={{ color: "#0369A1" }}>{selectAllCount}</strong> מתאמנים
                            המתאימים לסינון הנוכחי.
                        </Typography>
                        <Box sx={{ p: 2, backgroundColor: "#F1F5F9", borderRadius: "8px", mb: 2 }}>
                            <Typography sx={{ fontSize: "13px", color: "#64748B", mb: 1 }}>
                                סינון פעיל:
                            </Typography>
                            <Typography sx={{ fontSize: "14px" }}>
                                • מגדר: {gender === 'ALL' ? 'כל המגדרים' : gender === 'MALE' ? 'זכר' : 'נקבה'}
                            </Typography>
                            {keyword && (
                                <Typography sx={{ fontSize: "14px" }}>
                                    • חיפוש: "{keyword}"
                                </Typography>
                            )}
                            {trainerId !== 0 && trainers.find(t => t.id === trainerId) && (
                                <Typography sx={{ fontSize: "14px" }}>
                                    • מאמן: {trainers.find(t => t.id === trainerId)?.name}
                                </Typography>
                            )}
                        </Box>
                        <Alert severity="warning" sx={{ direction: "rtl" }}>
                            שים לב: פעולה זו תחליף את התוכנית הקיימת לכל המתאמנים שנבחרו
                        </Alert>
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ direction: 'ltr', gap: 1, p: 2 }}>
                <Button
                    onClick={() => setSelectAllDialogOpen(false)}
                    variant="outlined"
                >
                    ביטול
                </Button>
                <Button
                    onClick={handleSelectAllConfirm}
                    disabled={selectAllCount === 0 || loadingSelectAllCount}
                    variant="contained"
                    sx={{ backgroundColor: "#0EA5E9", "&:hover": { backgroundColor: "#0284C7" } }}
                >
                    הקצה ל-{selectAllCount} מתאמנים
                </Button>
            </DialogActions>
        </MuiDialog>
        )}
    </>
    );
};

export default UserMultiSelectDialog;
